import { z } from "zod";

import { slugString } from "./schemas/slug.js";
import animationsJson from "./data/exercise-animations.json" with { type: "json" };

// AN-1 — the committed slug → asset bridge, generated by
// `pnpm animations:manifest` (apps/api/scripts/animationManifest.ts) and
// reviewed as a git diff. Same shape of loader as descriptions.ts and
// taxonomy.ts: validated at module load, so drift surfaces during build
// rather than the first time an athlete taps the "?" on an exercise.
//
// ⛔ This file maps slugs to asset NAMES. It says nothing about where the
// GIFs are served from — that is AN-3, and the seam is deliberate: the
// mount resolves a URL (local file, S3, CDN) and hands packages/ui a
// resolved URL or null. Bundling the GIFs was ruled out; they are ~263 MB
// and grow every month.
const AnimationEntry = z.object({
  /** Basename of the purchased zip, e.g. `01561301`. */
  assetId: z.string().min(1),
  /** GIF filename minus `_<width>.gif` — enough to build a URL. */
  file: z.string().min(1),
  /** GymVisual's product id. Traceability back to the purchase only. */
  gymvisualProductId: z.number().int().positive(),
  /** Square widths available, ascending, e.g. `[180, 360, 720, 1080]`. */
  resolutions: z.array(z.number().int().positive()).min(1),
});

// Keyed by the catalog-slug regex — same shape as Exercise.slug, so a
// typo like "Cable Curl" or "cable_curl" in the generated JSON fails at
// module load instead of never resolving at lookup time.
const Manifest = z.record(slugString(64), AnimationEntry);
const parsed = Manifest.parse(animationsJson);

export type ExerciseAnimation = z.infer<typeof AnimationEntry>;

export const exerciseAnimations: Readonly<Record<string, ExerciseAnimation>> =
  parsed;

/**
 * The animation for an exercise, or null.
 *
 * ⛔ null is the NORMAL state, not an error: most of the catalog has no
 * animation and will stay that way for a long time. Callers render what
 * they rendered before — no placeholder, no "missing" badge.
 */
export const exerciseAnimationFor = (
  slug: string,
): ExerciseAnimation | null => parsed[slug] ?? null;

/**
 * Le larghezze effettivamente SERVITE, in ordine crescente.
 *
 * ⛔ Sono due, non le quattro del manifest, ed è una decisione di AN-3: con
 * l'animazione a piena larghezza il 180 e il 360 non li chiede nessuna
 * superficie, e caricarli vorrebbe dire mantenerli. Il manifest continua a
 * dichiarare tutto ciò che lo zip contiene — è un inventario, non un catalogo
 * di ciò che è online.
 */
export const ANIMATION_SERVED_WIDTHS = [720, 1080] as const;

/**
 * L'URL dell'animazione di un esercizio per una larghezza misurata in pixel
 * di dispositivo, o `null`.
 *
 * `null` è la risposta NORMALE in tre casi diversi, e nessuno dei tre è un
 * errore: l'host non ha una base (le animazioni non sono configurate su questo
 * canale), l'esercizio non ha un'animazione (la maggior parte del catalogo), o
 * lo slug non esiste. Chi chiama rende quello che rendeva prima.
 *
 * ⭐ Si prende il taglio più piccolo che COPRE la larghezza chiesta: sotto i
 * 720 pixel un 1080 sarebbe 1,6 MB per una miniatura. Sopra, il 1080 è il più
 * grande che esista e ridimensionarlo in su lo fa sgranare comunque meno che
 * ingrandire il 720.
 *
 * ⛔ La funzione è SINCRONA apposta: è il contratto di `AnimationSourceFn` in
 * `packages/ui`, che viene chiamata durante il render. Niente qui può fare una
 * fetch — il file arriva dopo, ed è `<Image>` a scaricarlo.
 */
export const animationUrlFor = (
  baseUrl: string | undefined,
  slug: string,
  widthPx: number,
): string | null => {
  if (!baseUrl) return null;
  const entry = exerciseAnimationFor(slug);
  if (!entry) return null;
  const width =
    widthPx <= ANIMATION_SERVED_WIDTHS[0]
      ? ANIMATION_SERVED_WIDTHS[0]
      : ANIMATION_SERVED_WIDTHS[1];
  return `${baseUrl.replace(/\/+$/, "")}/${entry.assetId}/${width}.gif`;
};
